import { canEditRecord } from '../../lib/recordAccess';
import * as React from 'react';
import Box from '@mui/material/Box';
import { Button, Chip, Collapse, FormControl, IconButton, InputLabel, MenuItem, Select, Slider, TextField, Tooltip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { API } from '../../lib/api';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import ContactsIcon from '@mui/icons-material/Contacts';
import ContactsConfig from "../../lib/config/contact";
import RecordModal from '../RecordModal/RecordModal';
import { FormProvider } from '../../contexts/CreateRecordFormContext';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import OpportunityContactSelectModal from './OpportunityContactSelectModal';
// import { jwtDecode } from 'jwt-decode';

const salesStageOptions = ["01 - Lead", "02 - Identified", "06 - Quote Sent", "08 - Won"];

const surfaceSx = {
    border: "1px solid",
    borderColor: "var(--hive-overview-surface-border, rgba(15, 23, 42, 0.08))",
    borderRadius: 3,
    p: { xs: 2, md: 2.5 },
    background: "var(--hive-overview-surface-bg, #fff)",
    boxShadow: "var(--hive-overview-surface-shadow, 0 10px 30px rgba(15, 23, 42, 0.04))",
    color: "var(--hive-overview-surface-text, inherit)",
    backdropFilter: "var(--hive-overview-surface-blur, none)",
    WebkitBackdropFilter: "var(--hive-overview-surface-blur, none)"
};

const fieldGridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
    gap: { xs: 1.25, md: 1.5 }
};

const normalizeDateValue = (value) => {
    if (!value) return "";
    const date = dayjs(value);
    return date.isValid() ? date.format("YYYY-MM-DD") : "";
};

const getReferenceId = (value) => {
    if (!value) return "";
    if (typeof value === "object") {
        return value._id || value.value || "";
    }
    return value;
};

const OpportunityOverview = ({ project, setFeedback, setLogs, setProject }) => {
    const [collapsed, setCollapsed] = React.useState(false);
        const [openCreateModal, setOpenCreateModal] = React.useState(false);
    const [loading, setLoading] = React.useState(true)
    const [quoteNumber, setQuote] = React.useState("")
    const [quoteRevision, setRevision] = React.useState("")
    const [projectNumber, setProjectNumber] = React.useState("")
    const [name, setName] = React.useState("")
    const [salesStage, setStage] = React.useState("")

    const [closeDate, setCloseDate] = React.useState("")

    const [primaryContact, setPrimaryContact] = React.useState("")
    const [owner, setOwner] = React.useState("")
    const [winProbability, setWinProbability] = React.useState(0)
    const [customerContacts,setCustomerContacts] = React.useState([])
    const [allContacts, setAllContacts] = React.useState([])
    const [allContactsModal, setAllContactsModal] = React.useState(false)

    const [winColor, setWinColor] = React.useState("primary")
    const [users,setUsers] = React.useState([])

    const { user } = useAuth()
    const canEdit = canEditRecord(user, project);

    const handleOpenCreateModal = () => setOpenCreateModal(true);
    const handleCloseCreateModal = () => setOpenCreateModal(false);
    const handleCloseAllContactsModal = () => setAllContactsModal(false)

    const applyWinColor = React.useCallback((value) => {
        setWinProbability(value)
        if (value <= 35) {
            setWinColor("error")
        } else if (value > 35 && value <= 55 ) {
            setWinColor("warning")
        } else if (value > 55 && value <= 79) {
            setWinColor("primary")
        } else {
            setWinColor("success")
        }
    }, [])

    const refreshOpportunityData = React.useCallback(async () => {
        const [refreshedProject, fetchNewLogs] = await Promise.all([
            API.Opportunities.FetchById(project._id),
            API.OpportunityLogs.FetchByOppId(project._id)
        ])

        setProject(refreshedProject)
        setLogs(fetchNewLogs)
        return refreshedProject
    }, [project?._id, setLogs, setProject])

    const saveOpportunityChanges = React.useCallback(async (changes) => {
        if (!project?._id) return false

        const tempData = {
            ...changes,
            lastUpdatedBy: user.email,
            lastUpdatedDate: Date.now()
        }

        const response = await API.Opportunities.UpdateSingle(project._id, tempData)
        if (response.status === 200) {
            setFeedback({open: true, message: response.data.message, type: "success"})
            await refreshOpportunityData()
            return true
        } else {
            setFeedback({open: true, message: response?.data?.message ? response.data.message :`Failed to update Opportunity: ${project.name}`, type: "error"})
            return false
        }
    }, [project?._id, project?.name, refreshOpportunityData, setFeedback, user?.email])

    const fieldHasChanged = React.useCallback((field, value) => {
        if (!project) return false

        if (field === "closeDate") {
            return normalizeDateValue(project?.[field]) !== normalizeDateValue(value)
        }

        if (field === "primaryContact" || field === "owner") {
            return getReferenceId(project?.[field]) !== getReferenceId(value)
        }

        if (field === "winProbability") {
            return Number(project?.winProbability || 0) !== Number(value || 0)
        }

        return (project?.[field] ?? "") !== (value ?? "")
    }, [project])

    const handleCreateSubmit = async (data) => {
        const cid = project?.customer?._id

        if (!cid) {
            setFeedback(prevData => ({ ...prevData, open: true, message: "Cannot Resolve Customer ID...", type: "error" }))
            return
        }
        const response = await API.Contacts.Create(cid, data)

        if (response.status === 200) {
            const newContactOption = {label: response.data.data.name, value: response.data.data._id}
            setCustomerContacts(prev => prev.some(contact => contact.value === newContactOption.value) ? prev : [...prev, newContactOption])
            setAllContacts(prev => prev.some(contact => contact.value === newContactOption.value)
                ? prev
                : [...prev, { ...newContactOption, label: `${response.data.data.name} - ${project?.customer?.organizationName || ""}`.trim() }])
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }

        handleCloseCreateModal()
    }

    const handleContactChange = async (e) => {
        console.log(e)
        console.log(project)
    }

    const syncProjectState = React.useCallback((currentProject) => {
        setQuote(currentProject?.quoteNumber || "")
        setRevision(currentProject?.quoteRevision || "")
        setProjectNumber(currentProject?.projectNumber || "")
        setName(currentProject?.name || "")
        setStage(currentProject?.salesStage || "")

        setCloseDate(currentProject?.closeDate || "")

        setOwner(currentProject?.owner?._id || currentProject?.owner || "")
        setPrimaryContact(getReferenceId(currentProject?.primaryContact))
        applyWinColor(Number(currentProject?.winProbability || 0))
    }, [applyWinColor])

    const autoSaveField = async (field, value) => {
        if (!canEdit) return;
        if (!fieldHasChanged(field, value)) return

        const payloadValue = field === "closeDate"
            ? (value ? dayjs(value).toISOString() : null)
            : field === "winProbability"
                ? Number(value || 0)
                : value

        const success = await saveOpportunityChanges({ [field]: payloadValue })

        if (!success && project) {
            syncProjectState(project)
        }
    }

    React.useEffect(() => {
        let isMounted = true

        async function fetchUsers() {
            const response = await API.Users.FetchAll()
            if (!isMounted) return

            const tempUsers = response.map(user => ({
                value: user._id,
                label: `${user.firstName} ${user.lastName}`
            }))
            setUsers(tempUsers)
        }

        async function fetchCustomerContacts() {
            if (!project?.customer?._id) return

            const response = await API.Customers.FetchById(project.customer._id)
            if (!isMounted || !response) return

            const tempContacts = (response.contacts || []).map(contact => ({
                value: contact._id,
                label: contact.name
            }))
            setCustomerContacts(tempContacts)
        }

        fetchUsers()
        fetchCustomerContacts()

        if (project && Object.keys(project).length > 0) {
            syncProjectState(project)
            setLoading(false)
        }

        return () => {
            isMounted = false
        }
    }, [project, syncProjectState])

    React.useEffect(() => {
        if (!allContactsModal || allContacts.length > 0) return

        let isMounted = true

        async function fetchAllContacts() {
            const response = await API.Customers.FetchAll()
            if (!isMounted) return

            const flattenedContacts = response.flatMap(customer =>
                (customer.contacts || []).map(contact => ({
                    ...contact,
                    organizationName: customer.organizationName
                }))
            )

            const tempContacts = flattenedContacts.map(contact => ({
                value: contact._id,
                label: `${contact.name} - ${contact.organizationName}`
            }))

            setAllContacts(tempContacts)
        }

        fetchAllContacts()

        return () => {
            isMounted = false
        }
    }, [allContacts, allContacts.length, allContactsModal])

    const selectedOwnerLabel = users.find((person) => person.value === owner)?.label
        || [project?.owner?.firstName, project?.owner?.lastName].filter(Boolean).join(" ")
        || "Unassigned";

    const winProbabilityLabel = winProbability >= 80
        ? "Strong"
        : winProbability >= 56
            ? "Healthy"
            : winProbability >= 36
                ? "Watch"
                : "At Risk";

  return (
    loading ? null : (
        <FormProvider>
            <RecordModal open={openCreateModal} handleClose={handleCloseCreateModal} fields={ContactsConfig.createFields} title="Create New Contact" onSubmit={handleCreateSubmit} />
            <OpportunityContactSelectModal open={allContactsModal} handleClose={handleCloseAllContactsModal} contacts={allContacts} onSubmit={handleContactChange} />
            <div className="col-xl-12 col-md-12">
                <div className='card'>
                    <div className="card-header d-flex align-items-center justify-content-between">
                        {/* LEFT: Icon and title inline */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            onClick={() => setCollapsed(!collapsed)}
                            size="small"
                            sx={{ mr: 1 }}
                        >
                            {collapsed ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                        <h5 style={{ margin: 0 }}>
                            Overview - <span>{project?.name}</span>
                        </h5>
                        </Box>
                    </div>
                    <Collapse in={!collapsed}>
            <div className="card-body">
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.15fr 1fr" }, gap: 2.5, mt: 2, mb: 3 }}>
                    <Box
                        className="hive-overview-surface hive-overview-surface--hero"
                        sx={{ ...surfaceSx, background: "var(--hive-overview-hero-bg, linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, #ffffff 72%))" }}
                    >
                        <Typography variant="overline" sx={{ letterSpacing: "0.08em", color: "text.secondary" }}>
                            Opportunity Snapshot
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                            {name || project?.name || "Untitled Opportunity"}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            <Chip label={`Stage: ${salesStage || "Not set"}`} color="primary" variant="outlined" />
                            <Chip label={`Owner: ${selectedOwnerLabel}`} variant="outlined" />
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                            <TextField label="Quote #" value={quoteNumber || ""} InputProps={{ readOnly: true }} disabled fullWidth />
                            <TextField label="Revision" value={quoteRevision || ""} InputProps={{ readOnly: true }} disabled fullWidth />
                            <TextField label="Project #" value={projectNumber || ""} InputProps={{ readOnly: true }} disabled fullWidth />
                            <TextField
                                label="Account"
                                value={project?.customer?.organizationName || ""}
                                InputProps={{ readOnly: true }}
                                disabled
                                fullWidth
                            />
                        </Box>
                    </Box>

                    <Box
                        className="hive-overview-surface hive-overview-surface--probability"
                        sx={{ ...surfaceSx, background: "var(--hive-overview-panel-emphasis-bg, var(--hive-overview-surface-bg, #fff))" }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", mb: 2 }}>
                            <Box>
                                <Typography variant="overline" sx={{ letterSpacing: "0.08em", color: "text.secondary" }}>
                                    Win Probability
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                    Track confidence alongside the current stage.
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                    {winProbability || 0}%
                                </Typography>
                                <Chip label={winProbabilityLabel} color={winColor} size="small" sx={{ mt: 1 }} />
                            </Box>
                        </Box>
                        <Slider disabled={!canEdit}
                            onChange={(_, value) => applyWinColor(Array.isArray(value) ? value[0] : value)}
                            onChangeCommitted={(_, value) => autoSaveField("winProbability", Array.isArray(value) ? value[0] : value)}
                            valueLabelDisplay="auto"
                            color={winColor}
                            step={5}
                            marks
                            value={winProbability}
                        />
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                            Close Date: {closeDate ? dayjs(closeDate).format("MMM D, YYYY") : "Not scheduled"}
                        </Typography>
                    </Box>
                </Box>
                <Box className="hive-overview-surface" sx={{ ...surfaceSx, mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Summary
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
                        Core opportunity details stay together for faster scanning and editing.
                    </Typography>
                    <Box sx={fieldGridSx}>
                    <Box>
                        <FormControl fullWidth>
                            <TextField disabled={!canEdit}
                                value={name || ""}
                                onChange={(e) => setName(e.target.value)}
                                onBlur={() => autoSaveField("name", name)}
                                label="Name"
                                variant="outlined"
                            />
                        </FormControl>
                    </Box>

                    <Box>
                        <FormControl fullWidth>
                            <InputLabel id="sales-stage-label">Sales Stage</InputLabel>
                            <Select disabled={!canEdit}
                                labelId="sales-stage-label"
                                id="sales-stage-select"
                                value={salesStage || ""}
                                label="Sales Stage"
                                onChange={(e) => {
                                    const value = e.target.value
                                    setStage(value)
                                    autoSaveField("salesStage", value)
                                }}
                            >
                                {salesStageOptions.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box>
                        <FormControl fullWidth>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker disabled={!canEdit}
                                    label="Close Date"
                                    value={closeDate ? dayjs(closeDate) : null}
                                    onChange={(date) => {
                                        if (date && !dayjs(date).isValid()) return
                                        const nextValue = date ? dayjs(date).toISOString() : ""
                                        setCloseDate(nextValue)
                                        autoSaveField("closeDate", nextValue || null)
                                    }}
                                    format="MM/DD/YYYY"
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        </FormControl>        
                    </Box>

                    <Box>
                        <FormControl fullWidth>
                            <InputLabel id="primary-contact-label">Primary Contact</InputLabel>
                            <Select disabled={!canEdit}
                                labelId="primary-contact-label"
                                id="primary-contact-select"
                                value={primaryContact || ""}
                                label="Primary Contact"
                                onChange={(e) => {
                                    const value = e.target.value
                                    setPrimaryContact(value)
                                    autoSaveField("primaryContact", value)
                                }}
                            >
                            {customerContacts && customerContacts.length > 0 ? (
                                customerContacts.map(contact => (
                                    <MenuItem key={contact.value} value={contact.value}>{contact.label}</MenuItem>
                                ))
                            ) : null}
                            </Select>
                            <div style={{display: "flex", justifyContent: "flex-start", marginTop: "0.75rem"}}>
                                <Tooltip title={"Create a new contact for this account"}>
                                    <Button
                                        disabled={!canEdit} onClick={handleOpenCreateModal}
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ContactsIcon />}
                                        sx={{
                                            borderRadius: 999,
                                            textTransform: "none",
                                            fontWeight: 600,
                                            px: 1.75
                                        }}
                                    >
                                        Add New Contact
                                    </Button>
                                </Tooltip>
                                {/* <Tooltip title={"Can't Find Contact?"}>     
                                    <IconButton disabled={!canEdit} onClick={handleOpenAllContactsModal}>
                                        <NotListedLocation color={"secondary"} />
                                    </IconButton>
                                </Tooltip> */}
                            </div>
                        </FormControl>
                    </Box>

                    {users.length && (
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel id="owner-label">Owner</InputLabel>
                                <Select disabled={!canEdit}
                                    labelId="owner-label"
                                    id="owner-select"
                                    value={owner || ""}
                                    label="Owner"
                                    onChange={(e) => {
                                        const value = e.target.value
                                        setOwner(value)
                                        autoSaveField("owner", value)
                                    }}
                                >
                                {users.map(user => (
                                    <MenuItem key={user.value} value={user.value}>{user.label}</MenuItem>
                                ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    </Box>
                </Box>
            </div>

                    </Collapse>
        </div>
    </div>
        </FormProvider>
    )
  );
}

export default OpportunityOverview
